<template>
  <div v-if="isVisible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <!-- Loading State -->
      <div v-if="isLoading" class="modal-loading">
        <div class="loading-spinner"></div>
        <p>{{ $t('cancellation.loading', 'Loading booking details...') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="modal-error">
        <div class="error-icon">❌</div>
        <h3>{{ $t('cancellation.error', 'Unable to Load Booking') }}</h3>
        <p>{{ error }}</p>
        <button @click="closeModal" class="btn-primary">
          {{ $t('cancellation.close', 'Close') }}
        </button>
      </div>

      <!-- Cancellation Success -->
      <div v-else-if="isCancelled" class="modal-success">
        <div class="success-icon">✅</div>
        <h3>{{ $t('cancellation.successTitle', 'Appointment Cancelled') }}</h3>

        <!-- Refund issued -->
        <template v-if="refundResult && refundResult.refundIssued">
          <p>{{ $t('cancellation.successWithRefund', 'Your appointment has been cancelled. A refund of {amount} will be processed within 5–10 business days.', { amount: formatCurrencyWithSymbol(refundResult.refundAmount) }) }}</p>
        </template>

        <!-- Contact studio for refund -->
        <template v-else-if="refundResult && refundResult.contactStudio">
          <p>{{ $t('cancellation.successContactStudio', 'Your appointment has been cancelled. Please contact the studio directly to arrange your refund.') }}</p>
        </template>

        <!-- Default (cash booking or no payment) -->
        <template v-else>
          <p>{{ $t('cancellation.successMessage', 'Your appointment has been successfully cancelled.') }}</p>
        </template>

        <button @click="closeModal" class="btn-primary">
          {{ $t('cancellation.close', 'Close') }}
        </button>
      </div>

      <!-- Booking Details and Cancellation Form -->
      <div v-else-if="booking" class="modal-content">
        <!-- Header -->
        <div class="modal-header">
          <h2>{{ $t('cancellation.title', 'Cancel Appointment') }}</h2>
          <button @click="closeModal" class="close-btn">&times;</button>
        </div>

        <!-- Tenant blocks online self-cancellation inside the cancellation window: no cancel button, call instead.
             Set from GET lateCancellationBlocked or from a POST 403 LATE_CANCELLATION_BLOCKED (window passed while open).
             Rendered ABOVE the booking details so the call button is visible without scrolling on a phone. -->
        <template v-if="lateBlocked">
          <div class="outside-window-notice" role="status" aria-live="polite">
            <div class="notice-icon" aria-hidden="true">📞</div>
            <div>
              <strong>{{ $t('cancellation.lateBlockedTitle') }}</strong><br>
              <template v-if="studioPhoneHref">{{ $t('cancellation.lateBlockedText', { hours: lateBlockedHours }) }}</template>
              <template v-else>{{ $t('cancellation.lateBlockedNoPhone', { hours: lateBlockedHours }) }}</template>
              <template v-if="hasHoursRemaining"><br><strong>{{ $t('cancellation.hoursRemaining', { hours: booking.hoursUntilAppointment }) }}</strong></template>
            </div>
          </div>
          <div class="contact-studio-block contact-studio-block--call">
            <!-- Only a real tel: link; with no usable number there is no dead button, the notice above says to contact the studio. -->
            <a
              v-if="studioPhoneHref"
              :href="studioPhoneHref"
              class="btn-primary call-studio-link"
              :aria-label="`${$t('cancellation.callStudio')} ${studioPhone}`"
            >
              <span>{{ $t('cancellation.callStudio') }}</span>
              <span class="call-studio-number">{{ studioPhone }}</span>
            </a>
            <button type="button" @click="closeModal" :class="studioPhoneHref ? 'btn-secondary' : 'btn-primary'">
              {{ $t('cancellation.close', 'Close') }}
            </button>
          </div>
        </template>

        <!-- Booking Details -->
        <div class="booking-details">
          <h4>{{ $t('cancellation.bookingDetails', 'Booking Details') }}</h4>
          
          <div class="detail-grid">
            <div class="detail-item">
              <strong>{{ $t('cancellation.bookingId', 'ID') }}:</strong> {{ booking.id }}
            </div>
            <div class="detail-item">
              <strong>{{ $t('cancellation.customer', 'Customer') }}:</strong> {{ booking.customer.firstName }} {{ booking.customer.lastName }}
            </div>
            <div class="detail-item">
              <strong>{{ $t('cancellation.service', 'Service') }}:</strong> {{ booking.service.name }} ({{ booking.service.duration }} min)
            </div>
            <div class="detail-item">
              <strong>{{ $t('cancellation.massageType', 'Type') }}:</strong> {{ booking.massageType.name }}
            </div>
            <div class="detail-item">
              <strong>{{ $t('cancellation.date', 'Date') }}:</strong> {{ booking.appointmentDate }}
            </div>
            <div class="detail-item">
              <strong>{{ $t('cancellation.time', 'Time') }}:</strong> {{ booking.appointmentTime }}
            </div>
            <div class="detail-item">
              <strong>{{ $t('cancellation.persons', 'Persons') }}:</strong> {{ booking.numberOfPersons }}
            </div>
            <div class="detail-item total-price">
              <strong>{{ $t('cancellation.totalPrice', 'Total') }}:</strong> {{ formatCurrencyWithSymbol(booking.totalPrice) }}
            </div>
          </div>
        </div>

        <!-- Refund info: within window, online payment -->
        <div v-if="!lateBlocked && booking.isWithinCancellationWindow && booking.hasOnlinePayment" class="refund-info-notice">
          <div class="notice-icon">💳</div>
          <div>
            <strong>{{ $t('cancellation.refundNoticeTitle', 'Refund will be issued') }}</strong><br>
            {{ $t('cancellation.refundNoticeBody', 'A refund of {amount} will be processed (€5 cancellation fee applied).', { amount: formatCurrencyWithSymbol(booking.refundAmount) }) }}
          </div>
        </div>

        <!-- Outside cancellation window — contact studio -->
        <div v-if="!lateBlocked && !booking.isWithinCancellationWindow && booking.hasOnlinePayment" class="outside-window-notice">
          <div class="notice-icon">⚠️</div>
          <div>
            <strong>{{ $t('cancellation.outsideWindowTitle', 'Cancellation window has passed') }}</strong><br>
            {{ $t('cancellation.outsideWindowBody', 'Please contact the studio directly to claim your refund.') }}
          </div>
        </div>

        <!-- Short-notice warning for non-online bookings inside the tenant's cancellation window
             (lessThan24Hours is server-computed as hoursUntil < cancellationWindowHours despite its name). -->
        <div v-if="!lateBlocked && booking.lessThan24Hours && !booking.hasOnlinePayment" class="warning-notice">
          <div class="warning-icon">⚠️</div>
          <div>
            <strong>{{ $t('cancellation.warningTitle', 'Short Notice Cancellation') }}</strong><br>
            {{ $t('cancellation.warningMessage', { hours: cancellationWindowHours }) }}
            <template v-if="hasHoursRemaining"><br><strong>{{ $t('cancellation.hoursRemaining', { hours: booking.hoursUntilAppointment }) }}</strong></template>
          </div>
        </div>

        <!-- Contact studio — outside window with online payment: booking stays active, no cancel button -->
        <div v-if="!lateBlocked && !booking.isWithinCancellationWindow && booking.hasOnlinePayment" class="contact-studio-block">
          <p>{{ $t('cancellation.contactStudioInstruction', 'To request a refund, please contact the studio directly.') }}</p>
          <button @click="closeModal" class="btn-primary">
            {{ $t('cancellation.close', 'Close') }}
          </button>
        </div>

        <!-- Normal cancellation form (within window, or cash payment) -->
        <div v-else-if="!lateBlocked" class="cancellation-form">
          <h4>{{ $t('cancellation.confirmTitle', 'Confirm Cancellation') }}</h4>
          <div class="form-group">
            <label for="verify-email">{{ $t('cancellation.verifyEmail', 'Your email address') }}</label>
            <input
              id="verify-email"
              type="email"
              v-model="verificationEmail"
              :placeholder="$t('cancellation.verifyEmailPlaceholder', 'Enter the email used when booking')"
              :disabled="isProcessing"
              class="verify-email-input"
              required
            />
          </div>
          <div class="form-group">
            <label for="reason">{{ $t('cancellation.reasonLabel', 'Reason (optional)') }}:</label>
            <textarea id="reason" v-model="cancellationReason"
              :placeholder="$t('cancellation.reasonPlaceholder', 'Please let us know why you need to cancel...')"
              rows="2"></textarea>
          </div>
          <div class="modal-actions">
            <button @click="confirmCancellation" :disabled="isProcessing || !verificationEmail.trim()" class="btn-danger">
              <span v-if="isProcessing">{{ $t('cancellation.processing', 'Processing...') }}</span>
              <span v-else>{{ $t('cancellation.confirmCancel', 'Yes, Cancel Appointment') }}</span>
            </button>
            <button @click="closeModal" class="btn-secondary" :disabled="isProcessing">
              {{ $t('cancellation.keepBooking', 'Keep My Booking') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted } from 'vue';
import { useCurrency } from '../composables/useCurrency.js';
import { useBusinessSettings } from '../composables/useBusinessSettings.js';
import { useBookingSettings } from '../composables/useBookingSettings.js';

export default {
  name: 'CancellationModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    bookingId: {
      type: String,
      default: null
    }
  },
  emits: ['close'],
  setup() {
    const { formatCurrencyWithSymbol, fetchSystemPreferences } = useCurrency();
    // Fallbacks for the late-cancellation block: phone and window hours when the
    // cancellation API does not send them. Both stores are shared and cached.
    const { businessPhone, load: loadBusinessSettings } = useBusinessSettings();
    const { settings: bookingSettings, load: loadBookingSettings } = useBookingSettings();
    onMounted(() => {
      fetchSystemPreferences();
      loadBusinessSettings();
      loadBookingSettings();
    });
    return { formatCurrencyWithSymbol, businessPhone, bookingSettings };
  },
  data() {
    return {
      booking: null,
      isLoading: false,
      isProcessing: false,
      isCancelled: false,
      error: null,
      cancellationReason: '',
      refundResult: null,
      verificationEmail: '',
      lateBlocked: false,
      lateBlockedHoursOverride: null,
      lateBlockedPhone: null
    };
  },
  computed: {
    lateBlockedHours() {
      return this.lateBlockedHoursOverride
        ?? (this.booking && this.booking.cancellationWindowHours)
        ?? this.bookingSettings.cancellationHours;
    },
    // Real window for this tenant (the GET sends it; booking settings are the fallback).
    cancellationWindowHours() {
      return (this.booking && this.booking.cancellationWindowHours) ?? this.bookingSettings.cancellationHours;
    },
    hasHoursRemaining() {
      const h = this.booking && this.booking.hoursUntilAppointment;
      return typeof h === 'number' && Number.isFinite(h) && h >= 0;
    },
    studioPhone() {
      const phone = this.lateBlockedPhone || this.businessPhone || '';
      return String(phone).trim();
    },
    studioPhoneHref() {
      // Keep digits and a single leading "+"; drop spaces, dashes, slashes, brackets.
      // "(0)" is the DE/AT/CH trunk-prefix notation ("+49 (0)30 ...") and must not be dialled
      // after the country code, so it is removed before the digits are kept.
      const international = /^(\+|00)/.test(this.studioPhone);
      const raw = international ? this.studioPhone.replace(/\(\s*0\s*\)/g, '') : this.studioPhone;
      const digits = raw.replace(/\D/g, '');
      if (!digits) return null;
      return `tel:${raw.startsWith('+') ? '+' : ''}${digits}`;
    }
  },
  watch: {
    isVisible(newValue) {
      if (newValue && this.bookingId) {
        this.loadBookingDetails();
      } else if (!newValue) {
        this.resetModal();
      }
    },
    bookingId(newValue) {
      if (newValue && this.isVisible) {
        this.loadBookingDetails();
      }
    }
  },
  methods: {
    async loadBookingDetails() {
      if (!this.bookingId) {
        this.error = this.$t('cancellation.invalidLink', 'Invalid cancellation link');
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const apiUrl = '';
        const response = await fetch(`${apiUrl}/api/bookings/cancellation/${this.bookingId}`);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to load booking details');
        }
        
        const data = await response.json();
        this.booking = data.data;

        if (this.booking.lateCancellationBlocked === true) {
          this.lateBlocked = true;
          this.lateBlockedPhone = this.booking.businessPhone || data.businessPhone || null;
        }

        // Override isWithinCancellationWindow using the customer's device clock.
        // new Date(`${date}T${time}`) with no timezone suffix is parsed as device local time,
        // which is the same timezone the customer used when reading the appointment — correct.
        if (this.booking.appointmentDateRaw && this.booking.appointmentTime) {
          const apptLocal = new Date(`${this.booking.appointmentDateRaw}T${this.booking.appointmentTime.trim()}:00`);
          const nowLocal = new Date();
          const hoursUntil = (apptLocal - nowLocal) / (1000 * 60 * 60);
          const GRACE_MINUTES = 5;
          this.booking.isWithinCancellationWindow = hoursUntil >= (this.booking.cancellationWindowHours - GRACE_MINUTES / 60);
          // Override the server-computed value (which uses UTC, not local time) with the
          // client's local device clock — same source used for the cancellation window check.
          this.booking.hoursUntilAppointment = Math.floor(hoursUntil);
        }

        if (!this.booking.canCancel && !this.lateBlocked) {
          this.error = this.$t('cancellation.cannotCancel', 'This booking cannot be cancelled');
        }
        
      } catch (error) {
        console.error('Error loading booking:', error);
        this.error = error.message;
      } finally {
        this.isLoading = false;
      }
    },

    async confirmCancellation() {
      this.isProcessing = true;

      try {
        const apiUrl = '';
        const response = await fetch(`${apiUrl}/api/bookings/cancellation/${this.booking.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: this.cancellationReason,
            email: this.verificationEmail.trim(),
            clientNow: (() => {
              const d = new Date();
              const pad = n => String(n).padStart(2, '0');
              // Naive local datetime string — no timezone suffix so server treats it as wall-clock.
              return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
            })()
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // The cancellation window closed while the modal was open: switch to the call-us state.
          if (response.status === 403 && errorData.code === 'LATE_CANCELLATION_BLOCKED') {
            this.lateBlocked = true;
            if (errorData.cancellationHours != null) this.lateBlockedHoursOverride = errorData.cancellationHours;
            if (errorData.businessPhone) this.lateBlockedPhone = errorData.businessPhone;
            return;
          }
          throw new Error(errorData.message || 'Failed to cancel booking');
        }

        const data = await response.json();
        console.log('Booking cancelled successfully:', data);

        this.refundResult = data.data;
        this.isCancelled = true;

      } catch (error) {
        console.error('Error cancelling booking:', error);
        alert(this.$t('cancellation.errorMessage', 'Failed to cancel appointment. Please try again or contact us directly.'));
      } finally {
        this.isProcessing = false;
      }
    },

    closeModal() {
      this.$emit('close');
    },

    handleOverlayClick() {
      // Only close if not loading or processing
      if (!this.isLoading && !this.isProcessing) {
        this.closeModal();
      }
    },

    resetModal() {
      this.booking = null;
      this.isLoading = false;
      this.isProcessing = false;
      this.isCancelled = false;
      this.error = null;
      this.cancellationReason = '';
      this.refundResult = null;
      this.verificationEmail = '';
      this.lateBlocked = false;
      this.lateBlockedHoursOverride = null;
      this.lateBlockedPhone = null;
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--overlay-backdrop);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: var(--surface-card);
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-light);
  background: var(--gradient-brand);
  color: var(--text-on-gradient);
  border-radius: 12px 12px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  color: inherit;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.3s;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-btn:hover {
  opacity: 1;
  background: rgba(255,255,255,0.1);
}

.modal-content {
  padding: 0;
}

.modal-loading,
.modal-error,
.modal-success {
  text-align: center;
  padding: 40px 30px;
  color: var(--text-primary);
}

/* Same emphasis token as the booking detail labels, in case these states
   ever render bold copy. */
.modal-loading strong,
.modal-error strong,
.modal-success strong {
  color: var(--text-strong);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-light);
  border-top: 4px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon,
.success-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.booking-details {
  padding: 20px;
  border-bottom: 1px solid var(--border-light);
}

.booking-details h4 {
  color: var(--color-secondary);
  margin-bottom: 20px;
  font-size: 18px;
}

.detail-grid {
  display: grid;
  gap: 12px;
}

.detail-item {
  padding: 8px 0;
  border-bottom: 1px solid var(--border-light);
  display: flex;
  justify-content: space-between;
  color: var(--text-primary);
}

/* Labels ("Booking ID:", "Customer:", …) follow the same emphasis token the
   other modals use for <strong>. .total-price keeps its own accent colour. */
.detail-item:not(.total-price) strong {
  color: var(--text-strong);
}

.detail-item:last-child {
  border-bottom: none;
}

.total-price {
  background: var(--surface-muted);
  padding: 15px;
  border-radius: 8px;
  border: none !important;
  margin-top: 10px;
  color: var(--color-secondary);
  font-weight: 600;
  font-size: 16px;
}

.warning-notice {
  background: var(--color-warning-soft);
  border: 1px solid var(--color-warning);
  padding: 20px 30px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  color: var(--text-primary);
  border-left: 4px solid var(--color-warning);
}

.warning-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.refund-info-notice {
  background: var(--color-success-soft);
  border: 1px solid var(--color-success);
  border-left: 4px solid var(--color-success);
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  color: var(--text-primary);
}

.outside-window-notice {
  background: var(--color-warning-soft);
  border: 1px solid var(--color-warning);
  border-left: 4px solid var(--color-warning);
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  color: var(--text-primary);
}

.notice-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.contact-studio-block {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.contact-studio-block p {
  color: var(--text-primary);
  margin: 0 0 8px;
}

.contact-studio-block .btn-danger,
.contact-studio-block .btn-secondary {
  width: 100%;
}

.call-studio-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  text-align: center;
  text-decoration: none;
  box-sizing: border-box;
}

.call-studio-number {
  font-weight: 400;
}

/* The call link is an <a>, the Close control a <button>: give both the same font so the
   anchor renders exactly like the primary button (buttons do not inherit font by default). */
.contact-studio-block--call {
  border-bottom: 1px solid var(--border-light);
}

.contact-studio-block--call .btn-primary,
.contact-studio-block--call .btn-secondary {
  width: 100%;
  min-height: 44px;
  font: inherit;
  font-weight: 600;
  line-height: 1.25;
}

.call-studio-link:hover,
.call-studio-link:visited,
.call-studio-link:active {
  color: var(--color-on-primary);
  text-decoration: none;
}

.cancellation-form {
  padding: 20px;
}

.cancellation-form h4 {
  color: var(--text-strong);
  margin-bottom: 20px;
  font-size: 18px;
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-main);
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
}

.form-group textarea:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-soft);
}

.verify-email-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-main);
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  box-sizing: border-box;
}

.verify-email-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-soft);
}

.modal-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.btn-danger {
  background: var(--color-danger);
  color: var(--color-on-danger);
  border: none;
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-danger:hover:not(:disabled) {
  background: var(--color-danger);
  filter: brightness(0.92);
}

.btn-danger:disabled {
  background: var(--surface-muted);
  color: var(--text-muted);
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--surface-muted);
  color: var(--text-primary);
  border: none;
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-secondary:hover:not(:disabled) {
  background: var(--surface-hover);
}

.btn-primary {
  background: var(--color-secondary);
  color: var(--color-on-primary);
  border: none;
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-primary:hover {
  background: var(--color-primary);
}

@media (prefers-reduced-motion: reduce) {
  .modal-container {
    animation: none;
  }

  .btn-primary,
  .btn-secondary,
  .btn-danger {
    transition: none;
  }
}

@media (max-width: 768px) {
  .modal-container {
    margin: 10px;
    max-height: 95vh;
  }
  
  .modal-header {
    padding: 20px;
  }
  
  .modal-header h2 {
    font-size: 20px;
  }
  
  .booking-details,
  .cancellation-form {
    padding: 20px;
  }
  
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .btn-danger,
  .btn-secondary,
  .btn-primary {
    width: 100%;
    justify-content: center;
  }
}
</style>